/**
 * Shared-video frame extraction + crop for face thumbnails.
 *
 * Per videoUrl we keep a single off-DOM `HTMLVideoElement` (lazy, cached
 * for the page session) and serialise seek+draw requests through a queue.
 * Compared to creating a fresh `<video>` per thumbnail this saves N-1
 * metadata fetches, N-1 decoder warm-ups, and N-1 hardware decoder slots
 * — significant on mobile where browsers cap concurrent video decoders.
 *
 * Each request:
 *   1. Awaits the (one-time) `loadedmetadata` for the shared element.
 *   2. Waits its turn in the per-URL FIFO queue.
 *   3. Seeks the shared element to the requested time (skipped if already
 *      within EPSILON of the target), waits for `seeked`, draws the crop
 *      to a fresh canvas, and resolves with a dataURL.
 *
 * Optional AbortSignal allows the caller to drop a pending request when
 * the consumer unmounts.
 */

import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';

const logger = LogManager.getLogger('utility:faceThumbnailRenderer');

const CROP_PADDING = 0.12;
/** Seek skipped when current time is already within this many seconds. */
const SEEK_EPSILON = 0.05;

export type RenderFaceThumbnailInput = {
  videoUrl: string;
  /** Media time in seconds where the face was first detected. */
  time: number;
  /** Normalised [x, y, w, h] in 0–1 video coordinates. */
  box: [number, number, number, number];
  /** Output canvas size in pixels (square). */
  size: number;
  /** Optional abort signal — drops the request from the queue. */
  signal?: AbortSignal;
};

type QueuedTask = {
  time: number;
  box: [number, number, number, number];
  size: number;
  signal?: AbortSignal;
  resolve: (dataUrl: string) => void;
  reject: (err: Error) => void;
};

type SharedVideo = {
  el: HTMLVideoElement;
  ready: Promise<void>;
  queue: QueuedTask[];
  running: boolean;
};

const sharedVideos = new Map<string, SharedVideo>();

/**
 * Render a face thumbnail using a shared video element keyed by `videoUrl`.
 * Returns a JPEG dataURL.
 */
export function renderFaceThumbnail(
  input: RenderFaceThumbnailInput,
): Promise<string> {
  const { videoUrl, time, box, size, signal } = input;
  if (signal?.aborted) {
    return Promise.reject(new Error('aborted'));
  }
  const shared = getSharedVideo(videoUrl);
  return new Promise<string>((resolve, reject) => {
    const task: QueuedTask = { time, box, size, signal, resolve, reject };
    shared.queue.push(task);
    if (signal) {
      signal.addEventListener(
        'abort',
        () => {
          const idx = shared.queue.indexOf(task);
          if (idx >= 0) {
            shared.queue.splice(idx, 1);
            reject(new Error('aborted'));
          }
        },
        { once: true },
      );
    }
    void drainQueue(shared);
  });
}

function getSharedVideo(videoUrl: string): SharedVideo {
  const existing = sharedVideos.get(videoUrl);
  if (existing) return existing;

  const el = document.createElement('video');
  const separator = videoUrl.includes('?') ? '&' : '?';

  // Note: we append a query param to bypass cloudfront caching and CORS issues with cached videos that may not have the correct headers.
  el.src = `${videoUrl}${separator}_arborFaceThumb=1`;

  el.crossOrigin = 'anonymous';
  el.muted = true;
  el.preload = 'auto';
  el.playsInline = true;

  const ready = waitForEvent(el, 'loadedmetadata');
  const shared: SharedVideo = { el, ready, queue: [], running: false };
  sharedVideos.set(videoUrl, shared);
  return shared;
}

async function drainQueue(shared: SharedVideo): Promise<void> {
  if (shared.running) return;
  shared.running = true;
  try {
    await shared.ready;
    while (shared.queue.length > 0) {
      const task = shared.queue.shift();
      if (!task) break;
      if (task.signal?.aborted) {
        task.reject(new Error('aborted'));
        continue;
      }
      try {
        const dataUrl = await runTask(shared.el, task);
        task.resolve(dataUrl);
      } catch (error) {
        task.reject(error instanceof Error ? error : new Error(String(error)));
      }
    }
  } catch (error) {
    // Loading failed; reject everyone waiting and reset so a later request
    // can try again (rare — usually network/CORS).
    logger.error(
      'Shared thumbnail video failed to load',
      extractErrorInfo(error),
    );
    const queued = shared.queue.splice(0, shared.queue.length);
    for (const t of queued) {
      t.reject(error instanceof Error ? error : new Error(String(error)));
    }
  } finally {
    shared.running = false;
    // If new requests arrived while we were rejecting, kick again.
    if (shared.queue.length > 0) {
      void drainQueue(shared);
    }
  }
}

async function runTask(
  video: HTMLVideoElement,
  task: QueuedTask,
): Promise<string> {
  const targetTime = Math.max(0, task.time);
  if (Math.abs(video.currentTime - targetTime) >= SEEK_EPSILON) {
    const seekDone = waitForEvent(video, 'seeked');
    video.currentTime = targetTime;
    await seekDone;
  }
  if (task.signal?.aborted) throw new Error('aborted');

  const videoWidth = video.videoWidth;
  const videoHeight = video.videoHeight;
  if (!videoWidth || !videoHeight) {
    throw new Error('video has no intrinsic dimensions');
  }

  const [bx, by, bw, bh] = task.box;
  const padX = bw * CROP_PADDING;
  const padY = bh * CROP_PADDING;
  const sx = Math.max(0, (bx - padX) * videoWidth);
  const sy = Math.max(0, (by - padY) * videoHeight);
  const sw = Math.min(videoWidth - sx, (bw + padX * 2) * videoWidth);
  const sh = Math.min(videoHeight - sy, (bh + padY * 2) * videoHeight);

  const canvas = document.createElement('canvas');
  canvas.width = task.size;
  canvas.height = task.size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('failed to acquire 2d context');

  ctx.drawImage(video, sx, sy, sw, sh, 0, 0, task.size, task.size);
  return canvas.toDataURL('image/jpeg', 0.85);
}

function waitForEvent(target: HTMLVideoElement, name: string): Promise<void> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const onSuccess = () => {
      if (settled) return;
      settled = true;
      target.removeEventListener(name, onSuccess);
      target.removeEventListener('error', onError);
      resolve();
    };
    const onError = () => {
      if (settled) return;
      settled = true;
      target.removeEventListener(name, onSuccess);
      target.removeEventListener('error', onError);
      const error = new Error(`video '${name}' error`);
      logger.error('FaceThumbnail: video event error', {
        event: name,
        error: extractErrorInfo(error),
      });
      reject(error);
    };
    target.addEventListener(name, onSuccess);
    target.addEventListener('error', onError);
  });
}

/**
 * Test/escape hatch: drop the shared video for a URL. Safe to call at any
 * time — any in-flight task rejects with `aborted` semantics on the next
 * tick because the queue has been cleared.
 */
export function disposeSharedVideo(videoUrl: string): void {
  const shared = sharedVideos.get(videoUrl);
  if (!shared) return;
  sharedVideos.delete(videoUrl);
  try {
    shared.el.removeAttribute('src');
    shared.el.load();
  } catch (error) {
    logger.warn('Failed to release shared thumbnail video', {
      ...extractErrorInfo(error),
    });
  }
}
